import { Delete } from 'lucide-react'
import { cn } from '@/lib/utils'

interface NumericKeypadProps {
  value: string
  onChange: (next: string) => void
  /** Hide the decimal key — useful when only whole numbers make sense (e.g. PIN). */
  hideDecimal?: boolean
  /** Max number of characters the value can grow to. Useful for PIN. */
  maxLength?: number
  className?: string
  disabled?: boolean
  /** Shorter keys, for a keypad that shares a dialog with other questions. */
  compact?: boolean
}

/**
 * On-screen numeric keypad for cashiers using a tablet or touch screen.
 * Always additive — the bound input remains focusable and editable from a
 * physical keyboard, so power users can still type.
 */
export function NumericKeypad({
  value,
  onChange,
  hideDecimal = false,
  maxLength,
  className,
  disabled,
  compact = false,
}: NumericKeypadProps) {
  const append = (ch: string) => {
    if (disabled) return
    if (ch === '.') {
      if (value.includes('.')) return
      const next = value === '' ? '0.' : value + '.'
      if (maxLength != null && next.length > maxLength) return
      onChange(next)
      return
    }
    // digit
    if (maxLength != null && value.length >= maxLength) return
    // collapse a leading "0" so users typing "5" don't end up with "05",
    // but keep "0." intact.
    if (value === '0') {
      onChange(ch)
      return
    }
    onChange(value + ch)
  }

  const backspace = () => {
    if (disabled) return
    if (!value) return
    onChange(value.slice(0, -1))
  }

  type Key =
    | { kind: 'digit'; label: string; press: () => void }
    | { kind: 'placeholder' }
    | { kind: 'backspace' }

  const keys: Key[] = [
    { kind: 'digit', label: '7', press: () => append('7') },
    { kind: 'digit', label: '8', press: () => append('8') },
    { kind: 'digit', label: '9', press: () => append('9') },
    { kind: 'digit', label: '4', press: () => append('4') },
    { kind: 'digit', label: '5', press: () => append('5') },
    { kind: 'digit', label: '6', press: () => append('6') },
    { kind: 'digit', label: '1', press: () => append('1') },
    { kind: 'digit', label: '2', press: () => append('2') },
    { kind: 'digit', label: '3', press: () => append('3') },
    hideDecimal
      ? { kind: 'placeholder' }
      : { kind: 'digit', label: '.', press: () => append('.') },
    { kind: 'digit', label: '0', press: () => append('0') },
    { kind: 'backspace' },
  ]

  return (
    <div
      className={cn('grid grid-cols-3 gap-2 select-none', className)}
      role="group"
      aria-label="Numeric keypad"
    >
      {keys.map((key, idx) => {
        if (key.kind === 'placeholder') {
          return <span key={idx} aria-hidden="true" />
        }
        const isBackspace = key.kind === 'backspace'
        return (
          <button
            key={idx}
            type="button"
            onClick={isBackspace ? backspace : key.press}
            disabled={disabled}
            aria-label={isBackspace ? 'Backspace' : key.label}
            // Prevent the keypad button from stealing focus from the input,
            // so the OS keyboard / caret state stay put.
            onMouseDown={(e) => e.preventDefault()}
            className={cn(
              compact
                ? 'h-11 rounded-lg border bg-background text-xl font-semibold tabular-nums'
                : 'h-14 sm:h-16 rounded-xl border bg-background text-2xl font-semibold tabular-nums',
              'active:scale-[0.97] transition-transform',
              'hover:bg-muted/60',
              'flex items-center justify-center',
              'disabled:opacity-30 disabled:pointer-events-none',
            )}
          >
            {isBackspace ? <Delete className="h-6 w-6" aria-hidden="true" /> : key.label}
          </button>
        )
      })}
    </div>
  )
}
